'use client'

import { useIsMounted } from '@repo/hooks'
import { Monitor, Moon, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'

import { Button } from '../_base/Button'
import { Text } from '../typography/Text'

const sizeConfig = {
  sm: { button: 'iconSm', icon: 'size-3' },
  md: { button: 'icon', icon: 'size-4' },
  lg: { button: 'iconLg', icon: 'size-5' }
} as const

type DarkModeToggleSize = keyof typeof sizeConfig

interface DarkModeToggleProps {
  size?: DarkModeToggleSize
}

export function DarkModeToggle({ size = 'sm' }: DarkModeToggleProps) {
  const config = sizeConfig[size]
  const { theme, setTheme } = useTheme()
  // The resolved theme is only known on the client (next-themes reads it in an
  // effect), so the button shell renders server-side for a stable layout but the
  // theme-dependent icon/label are deferred to after mount — otherwise the server
  // ("undefined") and client ("system") aria-labels/icons mismatch on hydration.
  const mounted = useIsMounted()

  const cycleTheme = () => {
    switch (theme) {
      case 'light':
        setTheme('dark')
        break
      case 'dark':
        setTheme('system')
        break
      case 'system':
      default:
        setTheme('light')
        break
    }
  }

  const getIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className={config.icon} />
      case 'dark':
        return <Moon className={config.icon} />
      case 'system':
      default:
        return <Monitor className={config.icon} />
    }
  }

  return (
    <Button
      variant="secondary"
      size={config.button}
      onClick={cycleTheme}
      aria-label={mounted ? `Current theme: ${theme}` : 'Toggle theme'}
    >
      {mounted ? getIcon() : <Monitor className={config.icon} />}
      <Text className="sr-only">Toggle theme</Text>
    </Button>
  )
}
